import React, { forwardRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { SLIDE_LAYOUTS, normalizeTipografia } from '../utils/slideSchema';
import { resolveSlideTheme } from '../utils/slideThemes';
import SlideChart from './SlideChart';
import SlideMediaLayer from './SlideMediaLayer';

function EditableText({ value, onChange, variant = 'body1', sx = {}, multiline = false, placeholder }) {
  const handleBlur = useCallback(
    (e) => {
      const next = e.currentTarget.textContent || '';
      if (next !== value) onChange(next);
    },
    [onChange, value]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!multiline && e.key === 'Enter') {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
    [multiline]
  );

  return (
    <Typography
      component="div"
      variant={variant}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      data-placeholder={placeholder}
      sx={{
        outline: 'none',
        cursor: 'text',
        borderRadius: 1,
        transition: 'background 0.15s',
        '&:hover': { bgcolor: 'rgba(56,189,248,0.08)' },
        '&:focus': {
          bgcolor: 'rgba(56,189,248,0.12)',
          boxShadow: '0 0 0 2px rgba(56,189,248,0.35)',
        },
        '&:empty:before': {
          content: 'attr(data-placeholder)',
          color: 'rgba(148,163,184,0.7)',
          fontStyle: 'italic',
        },
        ...sx,
      }}
    >
      {value}
    </Typography>
  );
}

const SlidePreview = forwardRef(function SlidePreview(
  { slide, onChange, emptyMessage, embedded = false, themeId },
  ref
) {
  const theme = resolveSlideTheme(themeId || slide?.theme);
  const tipografia = normalizeTipografia(slide?.tipografia);

  if (!slide) {
    return (
      <Box
        ref={ref}
        sx={{
          width: embedded ? '100%' : undefined,
          height: embedded ? '100%' : undefined,
          minHeight: embedded ? 0 : 360,
          borderRadius: embedded ? 0 : 2,
          border: embedded ? 'none' : `2px dashed ${theme.muted}`,
          bgcolor: embedded ? theme.bg : '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Typography sx={{ color: theme.muted }} textAlign="center">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const patch = (field, value) => onChange({ ...slide, [field]: value });

  const patchBullet = (index, value) => {
    const bullets = [...slide.bullets];
    bullets[index] = value;
    onChange({ ...slide, bullets });
  };

  const addBullet = () => onChange({ ...slide, bullets: [...slide.bullets, ''] });

  const removeBullet = (index) => {
    const bullets = slide.bullets.filter((_, i) => i !== index);
    onChange({ ...slide, bullets: bullets.length ? bullets : [''] });
  };

  const midias = slide.midias || [];
  const graficos = slide.graficos || [];

  const isCenter = slide.layout === SLIDE_LAYOUTS.TITULO_CENTRO;
  const isTwoCol = slide.layout === SLIDE_LAYOUTS.DUAS_COLUNAS;
  const textAlign = tipografia.align === 'center' || isCenter ? 'center' : tipografia.align || 'left';

  const mid = Math.ceil(slide.bullets.length / 2);
  const colA = isTwoCol ? slide.bullets.slice(0, mid) : slide.bullets;
  const colB = isTwoCol ? slide.bullets.slice(mid) : [];

  const renderBulletList = (items, offset = 0) => (
    <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'disc' }}>
      {items.map((b, i) => {
        const idx = offset + i;
        return (
          <Box
            component="li"
            key={`bullet-${idx}`}
            sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EditableText
                value={b}
                onChange={(v) => patchBullet(idx, v)}
                variant="body1"
                multiline
                placeholder="Bullet editável"
                sx={{
                  color: theme.muted,
                  fontSize: `${tipografia.bulletSize || 15}px`,
                  fontFamily: tipografia.fontFamily,
                  textAlign,
                }}
              />
            </Box>
            {slide.bullets.length > 1 && (
              <Tooltip title="Remover item">
                <IconButton
                  className="arca-export-hide"
                  size="small"
                  onClick={() => removeBullet(idx)}
                  sx={{ color: '#64748b', mt: -0.5 }}
                >
                  <RemoveCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        width: embedded ? '100%' : undefined,
        height: embedded ? '100%' : undefined,
        minHeight: embedded ? 0 : 360,
        borderRadius: embedded ? 0 : 2,
        bgcolor: theme.bg,
        color: theme.text,
        p: embedded ? { xs: 2, md: 3 } : { xs: 2.5, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isCenter ? 'center' : 'flex-start',
        textAlign,
        fontFamily: tipografia.fontFamily,
        boxShadow: embedded ? 'none' : '0 20px 50px rgba(15,23,42,0.25)',
        aspectRatio: embedded ? undefined : { md: '16/9' },
        overflow: embedded ? 'hidden' : 'visible',
      }}
    >
      <SlideMediaLayer
        midias={midias}
        onChange={(next) => onChange({ ...slide, midias: next })}
      />

      <EditableText
        value={slide.titulo}
        onChange={(v) => patch('titulo', v)}
        variant="h4"
        placeholder="Título do slide"
        sx={{
          position: 'relative',
          zIndex: 2,
          fontWeight: tipografia.boldTitulo ? 800 : 500,
          color: theme.text,
          fontSize: `${tipografia.tituloSize || 28}px`,
          fontFamily: tipografia.fontFamily,
          textAlign,
          mb: 1,
        }}
      />

      <EditableText
        value={slide.subtitulo}
        onChange={(v) => patch('subtitulo', v)}
        variant="subtitle1"
        placeholder="Subtítulo"
        sx={{
          position: 'relative',
          zIndex: 2,
          color: theme.muted,
          mb: isCenter ? 0 : 1.5,
          fontSize: `${tipografia.subtituloSize || 16}px`,
          fontFamily: tipografia.fontFamily,
          textAlign,
        }}
      />

      {graficos.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: graficos.length > 1 ? '1fr 1fr' : '1fr',
            },
            gap: 2,
            mb: 2,
          }}
        >
          {graficos.map((g) => (
            <SlideChart key={g.id} grafico={g} />
          ))}
        </Box>
      )}

      {!isCenter && (
        <Box sx={{ flex: 1, position: 'relative', zIndex: 2 }}>
          {isTwoCol ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>{renderBulletList(colA, 0)}</Box>
              <Box>{renderBulletList(colB, mid)}</Box>
            </Box>
          ) : (
            renderBulletList(colA)
          )}
          <Tooltip title="Adicionar bullet">
            <IconButton className="arca-export-hide" onClick={addBullet} sx={{ color: theme.accent, mt: 0.5 }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
});

export default SlidePreview;
